import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { TVResultMatch } from '@/utils/tv-mode-data';
import {
  TV_PALETTE,
  TV_RESULTS_HEIGHT,
  TV_RESULTS_SPOTLIGHT_MS,
  TV_RESULTS_TICKER_PX_PER_SEC,
} from './tv-mode.constants';
import { useTVScale } from './TVScaleContext';
import { TVTeamAvatarStack } from './TVTeamAvatarStack';
import { EmptyStateGrupos } from './EmptyStateGrupos';
import { EmptyStateEliminatorias } from './EmptyStateEliminatorias';
import { NewResultBanner } from './NewResultBanner';
import type { TvFixturePresentation } from './tv-phase-detection';

const CARD_GAP_BASE_PX = 8;
const CARD_WIDTH_BASE_PX = 290;

/** Verde "ganador" — convención visual del Modo TV (igual que el bracket). */
const RESULTS_WINNER_STRONG = '#2FA821';
/**
 * Color fijo de "detalles" (categoría + pulse dot) en ambas columnas de
 * "Últimos resultados". Antes la 2da columna usaba accent2 (naranja); el
 * usuario prefiere verde uniforme para no romper la convención de color
 * del Modo TV.
 */
const RESULTS_DETAIL_COLOR = RESULTS_WINNER_STRONG;

/** Divide el nombre de equipo (formato "P1 / P2") en líneas por jugador. */
const splitTeamPlayers = (teamName: string): string[] => {
  if (!teamName) return [''];
  const parts = teamName.split(/\s*\/\s*/).filter(Boolean);
  return parts.length > 0 ? parts : [teamName];
};
/** Solo recalcular mosaico si el viewport cambia al menos esto (evita tirones por subpixel). */
const VIEWPORT_WIDTH_HYSTERESIS_PX = 56;

/** Ancho de una pasada completa del orden [más reciente … más antiguo]. */
function cycleWidthPx(
  cardCount: number,
  cardWidth: number,
  cardGap: number,
): number {
  if (cardCount <= 0) return 0;
  return cardCount * cardWidth + Math.max(0, cardCount - 1) * cardGap;
}

interface Props {
  columns: Array<{
    tournamentName: string;
    animationDelay?: string;
    results: TVResultMatch[];
    phase: TvFixturePresentation;
  }>;
}

const ResultCard = ({ result }: { result: TVResultMatch }) => {
  const { s } = useTVScale();
  const w1 = !!result.team1Walkover;
  const w2 = !!result.team2Walkover;
  const isDoubleWalkover = w1 && w2;
  const team2Won = !isDoubleWalkover && !result.team1Won;
  const dimTeam1 = !result.team1Won || isDoubleWalkover;
  const dimTeam2 = result.team1Won || isDoubleWalkover;
  const team1NameColor = isDoubleWalkover
    ? TV_PALETTE.textSubtle
    : result.team1Won
      ? TV_PALETTE.navy
      : TV_PALETTE.textSubtle;
  const team2NameColor = isDoubleWalkover
    ? TV_PALETTE.textSubtle
    : team2Won
      ? TV_PALETTE.navy
      : TV_PALETTE.textSubtle;
  const team1NameWeight =
    result.team1Won && !isDoubleWalkover ? 700 : 400;
  const team2NameWeight = team2Won && !isDoubleWalkover ? 700 : 400;

  return (
    <div
      style={{
        flexShrink: 0,
        width: s(CARD_WIDTH_BASE_PX),
        background: TV_PALETTE.cardSoft,
        border: `1px solid ${TV_PALETTE.border}`,
        borderRadius: 8,
        padding: `${s(9)}px ${s(14)}px`,
      }}
    >
      <div
        style={{
          fontSize: s(12),
          color: RESULTS_DETAIL_COLOR,
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          marginBottom: s(6),
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        ✓ {result.category}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: s(5),
          gap: s(6),
          minWidth: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: s(6),
            minWidth: 0,
            flex: 1,
          }}
        >
          <TVTeamAvatarStack
            slots={result.team1Slots}
            size={s(24)}
            dimmed={dimTeam1}
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              flex: 1,
              lineHeight: 1.15,
            }}
          >
            {splitTeamPlayers(result.team1).map((player, i) => (
              <span
                key={`t1-${i}`}
                style={{
                  fontSize: s(14),
                  fontWeight: team1NameWeight,
                  color: team1NameColor,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  textDecoration: w1 ? 'line-through' : 'none',
                  textDecorationThickness: w1 ? 2 : undefined,
                }}
              >
                {player}
              </span>
            ))}
          </div>
        </div>
        <SetScoreRow
          sets={result.setScores}
          side={1}
          fontSize={s(20)}
          gap={s(6)}
        />
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: s(6),
          minWidth: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: s(6),
            minWidth: 0,
            flex: 1,
          }}
        >
          <TVTeamAvatarStack
            slots={result.team2Slots}
            size={s(24)}
            dimmed={dimTeam2}
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              flex: 1,
              lineHeight: 1.15,
            }}
          >
            {splitTeamPlayers(result.team2).map((player, i) => (
              <span
                key={`t2-${i}`}
                style={{
                  fontSize: s(14),
                  fontWeight: team2NameWeight,
                  color: team2NameColor,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  textDecoration: w2 ? 'line-through' : 'none',
                  textDecorationThickness: w2 ? 2 : undefined,
                }}
              >
                {player}
              </span>
            ))}
          </div>
        </div>
        <SetScoreRow
          sets={result.setScores}
          side={2}
          fontSize={s(20)}
          gap={s(6)}
        />
      </div>
    </div>
  );
};

/**
 * Renders each set's games as separate tokens, painting green only the ones
 * where the corresponding side won that specific set (instead of coloring the
 * whole row based on match winner).
 */
const SetScoreRow = ({
  sets,
  side,
  fontSize,
  gap,
}: {
  sets: { team1Games: number; team2Games: number }[];
  side: 1 | 2;
  fontSize: number;
  gap: number;
}) => {
  if (!sets || sets.length === 0) {
    return (
      <span
        className="font-space"
        style={{
          fontSize,
          fontWeight: 700,
          color: TV_PALETTE.textGhost,
          flexShrink: 0,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.01em',
        }}
      >
        -
      </span>
    );
  }
  return (
    <div
      className="font-space"
      style={{
        display: 'flex',
        gap,
        flexShrink: 0,
        fontVariantNumeric: 'tabular-nums',
        letterSpacing: '-0.01em',
      }}
    >
      {sets.map((set, i) => {
        const own = side === 1 ? set.team1Games : set.team2Games;
        const other = side === 1 ? set.team2Games : set.team1Games;
        const wonSet = own > other;
        return (
          <span
            key={i}
            style={{
              fontSize,
              fontWeight: 700,
              color: wonSet ? RESULTS_WINNER_STRONG : TV_PALETTE.textGhost,
            }}
          >
            {own}
          </span>
        );
      })}
    </div>
  );
};

const ResultCol = ({
  results,
  phase,
}: {
  results: TVResultMatch[];
  phase: TvFixturePresentation;
}) => {
  const { s } = useTVScale();
  const cardWidth = s(CARD_WIDTH_BASE_PX);
  const cardGap = s(CARD_GAP_BASE_PX);
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const segmentRef = useRef<HTMLDivElement>(null);
  const segment2Ref = useRef<HTMLDivElement>(null);
  const loopWidthRef = useRef(0);
  const offsetRef = useRef(0);
  const rafRef = useRef(0);
  const [viewportW, setViewportW] = useState(0);
  const [railMode, setRailMode] = useState<'ticker' | 'spotlight'>('ticker');
  const [spotlightMatch, setSpotlightMatch] = useState<TVResultMatch | null>(
    null,
  );
  const knownIdsRef = useRef<Set<string>>(new Set());
  const hasSeededRef = useRef(false);
  const spotlightQueueRef = useRef<TVResultMatch[]>([]);
  const spotlightIdRef = useRef<string | null>(null);
  const spotlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const railModeRef = useRef(railMode);
  const advanceSpotlightRef = useRef<() => void>(() => {});
  const resultsRef = useRef(results);
  resultsRef.current = results;

  useEffect(() => {
    railModeRef.current = railMode;
  }, [railMode]);

  useLayoutEffect(() => {
    advanceSpotlightRef.current = () => {
      if (spotlightTimerRef.current !== null) {
        clearTimeout(spotlightTimerRef.current);
        spotlightTimerRef.current = null;
      }
      const q = spotlightQueueRef.current;
      if (q.length === 0) {
        spotlightIdRef.current = null;
        setSpotlightMatch(null);
        setRailMode('ticker');
        return;
      }
      const next = q.shift()!;
      spotlightIdRef.current = next.id;
      setSpotlightMatch(next);
      setRailMode('spotlight');
      spotlightTimerRef.current = setTimeout(() => {
        advanceSpotlightRef.current();
      }, TV_RESULTS_SPOTLIGHT_MS);
    };
  });

  const resultsIdentity = useMemo(
    () => results.map((r) => r.id).join(','),
    [results],
  );

  useEffect(() => {
    const list = resultsRef.current;
    if (list.length === 0) {
      if (spotlightTimerRef.current !== null) {
        clearTimeout(spotlightTimerRef.current);
        spotlightTimerRef.current = null;
      }
      spotlightQueueRef.current = [];
      spotlightIdRef.current = null;
      knownIdsRef.current.clear();
      hasSeededRef.current = false;
      setSpotlightMatch(null);
      setRailMode('ticker');
      return;
    }

    if (!hasSeededRef.current) {
      for (const r of list) {
        knownIdsRef.current.add(r.id);
      }
      hasSeededRef.current = true;
      return;
    }

    const seen = knownIdsRef.current;
    for (const r of list) {
      if (!seen.has(r.id)) {
        seen.add(r.id);
        const q = spotlightQueueRef.current;
        const dup =
          q.some((m) => m.id === r.id) || spotlightIdRef.current === r.id;
        if (!dup) {
          q.push(r);
        }
      }
    }

    if (
      railModeRef.current === 'ticker' &&
      spotlightQueueRef.current.length > 0
    ) {
      advanceSpotlightRef.current();
    }
  }, [resultsIdentity]);

  useEffect(() => {
    return () => {
      if (spotlightTimerRef.current !== null) {
        clearTimeout(spotlightTimerRef.current);
      }
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const spotlightDisplay = useMemo(() => {
    if (!spotlightMatch) return null;
    return results.find((r) => r.id === spotlightMatch.id) ?? spotlightMatch;
  }, [results, spotlightMatch]);

  /** Repeticiones del ciclo ordenado hasta cubrir el ancho visible (sin hueco blanco al final). */
  const segmentItems = useMemo(() => {
    const n = results.length;
    if (n === 0) return [];
    const oneCycle = cycleWidthPx(n, cardWidth, cardGap);
    const vp = Math.max(viewportW, 400);
    const repeats = Math.max(2, Math.ceil((vp + oneCycle) / oneCycle));
    const out: { result: TVResultMatch; key: string }[] = [];
    for (let t = 0; t < repeats; t += 1) {
      results.forEach((r, i) => {
        out.push({ result: r, key: `${r.id}-t${t}-i${i}` });
      });
    }
    return out;
  }, [results, viewportW, cardWidth, cardGap]);

  const measureLoopWidth = () => {
    const s1 = segmentRef.current;
    const s2 = segment2Ref.current;
    if (!s1 || !s2) return;
    const d = s2.getBoundingClientRect().left - s1.getBoundingClientRect().left;
    if (d > 1) {
      const prev = loopWidthRef.current;
      const rounded = Math.round(d * 100) / 100;
      if (prev < 1 || Math.abs(prev - rounded) >= 0.75) {
        loopWidthRef.current = rounded;
      }
    }
  };

  useLayoutEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const readW = () => {
      const raw = Math.ceil(vp.getBoundingClientRect().width);
      const w = Math.max(400, raw);
      setViewportW((prev) => {
        if (prev === 0) return w;
        if (Math.abs(prev - w) >= VIEWPORT_WIDTH_HYSTERESIS_PX) return w;
        return prev;
      });
    };
    readW();
    let roFrame = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(roFrame);
      roFrame = requestAnimationFrame(readW);
    });
    ro.observe(vp);
    return () => {
      cancelAnimationFrame(roFrame);
      ro.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    let roFrame = 0;
    const scheduleMeasure = () => {
      cancelAnimationFrame(roFrame);
      roFrame = requestAnimationFrame(() => {
        measureLoopWidth();
      });
    };
    scheduleMeasure();
    const s1 = segmentRef.current;
    const s2 = segment2Ref.current;
    if (!s1 || !s2) return () => cancelAnimationFrame(roFrame);
    const ro = new ResizeObserver(scheduleMeasure);
    ro.observe(s1);
    ro.observe(s2);
    return () => {
      cancelAnimationFrame(roFrame);
      ro.disconnect();
    };
  }, [segmentItems]);

  const tickerIdentity = useMemo(
    () => results.map((r) => r.id).join(','),
    [results],
  );

  const tickerLayoutKey = `${tickerIdentity}|${segmentItems.length}`;

  /**
   * Con menos de 3 resultados el ticker queda repitiendo las mismas cards
   * en pantalla (loop visible) — preferimos renderizarlas estáticas.
   */
  const staticMode = results.length > 0 && results.length < 3;

  useEffect(() => {
    if (results.length === 0 || railMode !== 'ticker' || staticMode) {
      return () => cancelAnimationFrame(rafRef.current);
    }

    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      return () => cancelAnimationFrame(rafRef.current);
    }

    const track = trackRef.current;
    if (!track) {
      return () => cancelAnimationFrame(rafRef.current);
    }

    offsetRef.current = 0;
    track.style.transform = 'translate3d(0,0,0)';

    let last = performance.now();
    const speed = TV_RESULTS_TICKER_PX_PER_SEC;

    const tick = (now: number) => {
      const dt = Math.min(1 / 30, (now - last) / 1000);
      last = now;
      const el = trackRef.current;
      if (!el) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const loopW = loopWidthRef.current;
      if (loopW > 1) {
        offsetRef.current -= speed * dt;
        while (offsetRef.current <= -loopW) {
          offsetRef.current += loopW;
        }
        el.style.transform = `translate3d(${offsetRef.current}px,0,0)`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [tickerLayoutKey, railMode, results.length]);

  if (results.length === 0) {
    return (
      <div
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
          alignItems: 'stretch',
        }}
      >
        {phase === 'knockout-only' ? (
          <EmptyStateEliminatorias />
        ) : (
          <EmptyStateGrupos />
        )}
      </div>
    );
  }

  if (railMode === 'spotlight' && spotlightDisplay) {
    return (
      <div
        ref={viewportRef}
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
          alignItems: 'stretch',
        }}
      >
        <NewResultBanner result={spotlightDisplay} />
      </div>
    );
  }

  if (staticMode) {
    return (
      <div
        ref={viewportRef}
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          paddingLeft: s(6),
          paddingRight: s(6),
          gap: cardGap,
        }}
      >
        {results.map((r) => (
          <ResultCard key={r.id} result={r} />
        ))}
      </div>
    );
  }

  return (
    <div
      ref={viewportRef}
      style={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        paddingLeft: s(6),
        paddingRight: s(6),
      }}
    >
      <div
        ref={trackRef}
        style={{
          display: 'inline-flex',
          flexDirection: 'row',
          flexShrink: 0,
          gap: cardGap,
          willChange: 'transform',
        }}
      >
        <div
          ref={segmentRef}
          style={{
            display: 'flex',
            flexDirection: 'row',
            flexShrink: 0,
            gap: cardGap,
          }}
        >
          {segmentItems.map(({ result: r, key }) => (
            <ResultCard key={key} result={r} />
          ))}
        </div>
        <div
          ref={segment2Ref}
          style={{
            display: 'flex',
            flexDirection: 'row',
            flexShrink: 0,
            gap: cardGap,
          }}
          aria-hidden
        >
          {segmentItems.map(({ result: r, key }) => (
            <ResultCard key={`${key}-b`} result={r} />
          ))}
        </div>
      </div>
    </div>
  );
};

export const TVResultsBottom = ({ columns }: Props) => {
  const { s } = useTVScale();
  const accent = RESULTS_DETAIL_COLOR;
  const gridTemplate = columns.length === 2 ? '1fr 1fr' : '1fr';

  return (
    <div
      style={{
        background: TV_PALETTE.card,
        borderTop: `2px solid ${accent}`,
        flexShrink: 0,
        height: s(TV_RESULTS_HEIGHT),
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: gridTemplate,
          height: '100%',
          minHeight: 0,
        }}
      >
        {columns.map((col, idx) => (
          <div
            key={`${col.tournamentName}-${idx}`}
            style={{
              padding:
                idx === 0
                  ? `${s(8)}px ${s(14)}px ${s(8)}px ${s(16)}px`
                  : `${s(8)}px ${s(14)}px ${s(8)}px`,
              borderRight:
                idx < columns.length - 1
                  ? `1px solid ${TV_PALETTE.border}`
                  : undefined,
              display: 'flex',
              flexDirection: 'column',
              gap: s(4),
              overflowX: 'hidden',
              overflowY: 'visible',
              minWidth: 0,
              minHeight: 0,
            }}
          >
            {col.results.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: s(6),
                  flexShrink: 0,
                }}
              >
                <div
                  className="tv-anim-pulse"
                  style={{
                    width: s(6),
                    height: s(6),
                    borderRadius: '50%',
                    background: RESULTS_DETAIL_COLOR,
                    animationDelay: col.animationDelay,
                  }}
                />
                <span
                  style={{
                    fontSize: s(13),
                    fontWeight: 800,
                    color: TV_PALETTE.navy,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                  }}
                >
                  Últimos resultados
                </span>
                <span
                  style={{
                    fontSize: s(13),
                    color: TV_PALETTE.textSubtle,
                    fontWeight: 600,
                    marginLeft: 2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  · {col.tournamentName}
                </span>
              </div>
            )}
            <ResultCol results={col.results} phase={col.phase} />
          </div>
        ))}
      </div>
    </div>
  );
};
